import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { 
  MdDeleteSweep,
  MdEdit,
  MdRefresh,
  MdInfoOutline,
  MdContentCopy,
  MdCheck,
  MdCode,
  MdLink,
  MdSmartphone,
  MdPalette,
  MdClose
} from "react-icons/md";
import { apifetch } from '../services/interceptors';
import { 
  BiErrorCircle,
  BiCheckCircle,
  BiCopy,
  BiShow
} from "react-icons/bi";

import {
  Button,
  Table,
  Spinner,
  Modal,
  Alert,
  Badge,
  ButtonGroup,
  Tooltip,
  OverlayTrigger,
  Tabs,
  Tab,
  InputGroup,
  FormControl,
  Row,
  Col,
  Card
} from "react-bootstrap";

import { useTheme } from "../ThemeContext";

// Widget info modal component with dark mode support
const WidgetInfoModal = ({ show, onHide, widget }) => {
  const [activeTab, setActiveTab] = useState('embed');
  const [copied, setCopied] = useState({ embed: false, script: false, url: false });
  const { isDarkMode } = useTheme();

  if (!widget) return null;

  const handleCopy = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(prev => ({ ...prev, [type]: true }));
      setTimeout(() => {
        setCopied(prev => ({ ...prev, [type]: false }));
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Generate embed codes
  const widgetUrl = `https://app.pipsqueaknetscan.com/s2/public/widget/${widget.widgetKey}`;
  
  const iframeEmbed = `<iframe 
  src="${widgetUrl}" 
  style="width: 100%; border: none; min-height: 600px;" 
  title="${widget.title || 'Security Scanner'}"
  id="pipsqueak-widget-${widget.widgetKey}"
></iframe>

<script>
// Auto-resize iframe
window.addEventListener('message', function(event) {
  if (event.data.type === 'widget-resize' && event.data.widgetId === '${widget.widgetKey}') {
    const iframe = document.getElementById('pipsqueak-widget-${widget.widgetKey}');
    if (iframe) {
      iframe.style.height = event.data.height + 'px';
    }
  }
});
</script>`;

  const scriptEmbed = `<div id="widget-root"></div>

<script>
(function() {
  const script = document.createElement('script');
  script.src = '${widgetUrl}';
  script.async = true;
  script.onload = function() {
    if (window.PipsqueakWidget) {
      window.PipsqueakWidget.init({
        theme: '${widget.theme || 'light'}',
        widgetKey: '${widget.widgetKey}'
      });
    }
  };
  document.head.appendChild(script);
})();
</script>`;

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      className={`widget-info-modal ${isDarkMode ? 'dark-mode' : ''}`}
      contentClassName={isDarkMode ? "bg-dark text-light" : ""}
      data-bs-theme={isDarkMode ? 'dark' : 'light'}
    >
      <Modal.Header closeButton className={isDarkMode ? 'bg-dark text-light border-secondary' : ''}>
        <Modal.Title>
          <MdInfoOutline className="me-2" />
          Widget Integration Guide - {widget.title}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className={`mb-3 ${isDarkMode ? 'nav-tabs-dark' : ''}`}
          variant={isDarkMode ? 'pills' : 'tabs'}
          mountOnEnter={true}
          unmountOnExit={true}
        >
          <Tab eventKey="embed" title={
            <span>
              <MdCode className="me-1" /> Embed Code
            </span>
          }>
            <div>
              <h6 className={`mb-3 ${isDarkMode ? 'text-light' : ''}`}>Quick Integration Options</h6>
              
              {/* Widget URL */}
              <Card className={`mb-3 ${isDarkMode ? 'bg-dark border-secondary' : ''}`}>
                <Card.Body>
                  <Card.Title as="h6" className={`mb-3 ${isDarkMode ? 'text-light' : ''}`}>
                    <MdLink className="me-2" />
                    Widget URL
                  </Card.Title>
                  <InputGroup>
                    <FormControl
                      value={widgetUrl}
                      readOnly
                      aria-label="Widget URL"
                      className={isDarkMode ? 'bg-dark text-light border-secondary' : ''}
                    />
                    <Button
                      variant={isDarkMode ? "outline-light" : "outline-secondary"}
                      onClick={() => handleCopy(widgetUrl, 'url')}
                    >
                      {copied.url ? <MdCheck className="text-success" /> : <BiCopy />}
                    </Button>
                    <Button
                      variant="primary"
                      onClick={() => window.open(widgetUrl, '_blank')}
                    >
                      <BiShow className="me-1" />
                      Preview
                    </Button>
                  </InputGroup>
                </Card.Body>
              </Card>

              {/* Iframe Embed */}
              <Card className={`mb-3 ${isDarkMode ? 'bg-dark border-secondary' : ''}`}>
                <Card.Body>
                  <Card.Title as="h6" className={`mb-3 ${isDarkMode ? 'text-light' : ''}`}>
                    <MdCode className="me-2" />
                    Iframe Embed (Recommended)
                  </Card.Title>
                  <div className="code-block-container">
                    <pre className={`code-block p-3 rounded ${isDarkMode ? 'bg-secondary text-light' : 'bg-light'}`}>
                      <code>{iframeEmbed}</code>
                    </pre>
                    <Button
                      variant={isDarkMode ? "outline-light" : "outline-secondary"}
                      size="sm"
                      className="mt-2"
                      onClick={() => handleCopy(iframeEmbed, 'embed')}
                    >
                      {copied.embed ? <><MdCheck className="me-1" /> Copied!</> : <><BiCopy className="me-1" /> Copy Code</>}
                    </Button>
                  </div>
                </Card.Body>
              </Card>

              {/* Script Embed */}
              <Card className={`mb-3 ${isDarkMode ? 'bg-dark border-secondary' : ''}`}>
                <Card.Body>
                  <Card.Title as="h6" className={`mb-3 ${isDarkMode ? 'text-light' : ''}`}>
                    <MdCode className="me-2" />
                    Script Tag Embed
                  </Card.Title>
                  <div className="code-block-container">
                    <pre className={`code-block p-3 rounded ${isDarkMode ? 'bg-secondary text-light' : 'bg-light'}`}>
                      <code>{scriptEmbed}</code>
                    </pre>
                    <Button
                      variant={isDarkMode ? "outline-light" : "outline-secondary"}
                      size="sm"
                      className="mt-2"
                      onClick={() => handleCopy(scriptEmbed, 'script')}
                    >
                      {copied.script ? <><MdCheck className="me-1" /> Copied!</> : <><BiCopy className="me-1" /> Copy Code</>}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </div>
          </Tab>

          <Tab eventKey="preview" title={
            <span>
              <BiShow className="me-1" /> Preview
            </span>
          }>
            <div className="text-center">
              <div className="widget-preview-container">
                <iframe
                  src={widgetUrl}
                  title="Widget Preview"
                  style={{
                    width: '100%',
                    height: '500px',
                    border: `1px solid ${isDarkMode ? '#495057' : '#dee2e6'}`,
                    borderRadius: '8px',
                    backgroundColor: isDarkMode ? '#1a1e21' : '#ffffff'
                  }}
                />
              </div>
              <p className={`mt-3 mb-0 ${isDarkMode ? 'text-light-emphasis' : 'text-muted'}`}>
                This is a live preview of your widget. The actual appearance may vary based on your website's styling.
              </p>
            </div>
          </Tab>

          <Tab eventKey="settings" title={
            <span>
              <MdSmartphone className="me-1" /> Settings
            </span>
          }>
            <div>
              <Row>
                <Col md={6}>
                  <h6>Basic Settings</h6>
                  <dl className="row">
                    <dt className={`col-sm-5 ${isDarkMode ? 'text-light-emphasis' : ''}`}>Title:</dt>
                    <dd className="col-sm-7">{widget.title || '-'}</dd>

                    <dt className={`col-sm-5 ${isDarkMode ? 'text-light-emphasis' : ''}`}>Subtitle:</dt>
                    <dd className="col-sm-7">{widget.subtitle || '-'}</dd>

                    <dt className={`col-sm-5 ${isDarkMode ? 'text-light-emphasis' : ''}`}>Button Text:</dt>
                    <dd className="col-sm-7">
                      <Badge bg="primary">{widget.buttonText || '-'}</Badge>
                    </dd>

                    <dt className={`col-sm-5 ${isDarkMode ? 'text-light-emphasis' : ''}`}>Button Color:</dt>
                    <dd className="col-sm-7">
                      <span 
                        style={{
                          display: 'inline-block',
                          width: '20px',
                          height: '20px',
                          backgroundColor: widget.buttonColor || '#8b5cf6',
                          borderRadius: '4px',
                          marginRight: '8px',
                          verticalAlign: 'middle'
                        }}
                      />
                      {widget.buttonColor || '-'}
                    </dd>

                    <dt className={`col-sm-5 ${isDarkMode ? 'text-light-emphasis' : ''}`}>GoHighLevel:</dt>
                    <dd className="col-sm-7">
                      {widget.ghl_enabled && widget.ghl_has_token && widget.ghl_location_id ? 
                        <Badge bg="success">Active ✓</Badge> :
                        widget.ghl_enabled ?
                        <Badge bg="warning">Incomplete Setup</Badge> :
                        <Badge bg="secondary">Inactive</Badge>
                      }
                    </dd>

                    {widget.ghl_location_id && (
                      <>
                        <dt className={`col-sm-5 ${isDarkMode ? 'text-light-emphasis' : ''}`}>Location ID:</dt>
                        <dd className="col-sm-7">
                          <code className={isDarkMode ? 'text-light' : ''}>{widget.ghl_location_id}</code>
                        </dd>
                      </>
                    )}

                    {widget.ghl_has_token ? (
                      <>
                        <dt className={`col-sm-5 ${isDarkMode ? 'text-light-emphasis' : ''}`}>API Token:</dt>
                        <dd className="col-sm-7">
                          <Badge bg="info">🔒 Encrypted &amp; Stored</Badge>
                        </dd>
                      </>
                    ) : null}

                    {widget.ghl_source && (
                      <>
                        <dt className={`col-sm-5 ${isDarkMode ? 'text-light-emphasis' : ''}`}>GHL Source:</dt>
                        <dd className="col-sm-7">{widget.ghl_source}</dd>
                      </>
                    )}

                    <dt className={`col-sm-5 ${isDarkMode ? 'text-light-emphasis' : ''}`}>Webhook URL:</dt>
                    <dd className="col-sm-7">
                      {widget.webhookUrl ? 
                        <code className={isDarkMode ? 'text-light' : ''}>{widget.webhookUrl}</code> : 
                        <span className="text-muted">Not configured</span>
                      }
                    </dd>
                  </dl>
                </Col>
                <Col md={6}>
                  <h6>Display Settings</h6>
                  <dl className="row">
                    <dt className={`col-sm-5 ${isDarkMode ? 'text-light-emphasis' : ''}`}>Layout:</dt>
                    <dd className="col-sm-7">
                      <Badge bg="info">{widget.layout || '-'}</Badge>
                    </dd>

                    <dt className={`col-sm-5 ${isDarkMode ? 'text-light-emphasis' : ''}`}>Theme:</dt>
                    <dd className="col-sm-7">
                      <Badge bg="secondary">{widget.theme || '-'}</Badge>
                    </dd>

                    <dt className={`col-sm-5 ${isDarkMode ? 'text-light-emphasis' : ''}`}>Display Mode:</dt>
                    <dd className="col-sm-7">{widget.displayMode || '-'}</dd>

                    <dt className={`col-sm-5 ${isDarkMode ? 'text-light-emphasis' : ''}`}>Collect Email:</dt>
                    <dd className="col-sm-7">
                      {widget.collectEmail ? 
                        <Badge bg="success">Yes</Badge> : 
                        <Badge bg="warning">No</Badge>
                      }
                    </dd>
                  </dl>
                </Col>
              </Row>

              {widget.features && widget.features.length > 0 && (
                <>
                  <h6 className="mt-3">Features</h6>
                  <ul className="list-unstyled">
                    {widget.features.map((feature, idx) => (
                      <li key={idx} className="mb-1">
                        <BiCheckCircle className="text-success me-2" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              <div className={`mt-3 p-3 rounded ${isDarkMode ? 'bg-secondary' : 'bg-light'}`}>
                <small className={isDarkMode ? 'text-light-emphasis' : 'text-muted'}>
                  <strong>Created:</strong> {new Date(widget.createdAt).toLocaleString()}<br />
                  <strong>Widget Key:</strong> <code className={isDarkMode ? 'text-light bg-dark' : ''}>{widget.widgetKey}</code>
                </small>
              </div>
            </div>
          </Tab>
        </Tabs>
      </Modal.Body>

      <Modal.Footer className={isDarkMode ? 'bg-dark border-secondary' : ''}>
        <Button variant={isDarkMode ? "outline-light" : "secondary"} onClick={onHide}>
          <MdClose className="me-1" />
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// Edit Widget Modal with dark mode support
const EditWidgetModal = ({ show, onHide, widget, onSave }) => {
  const [formData, setFormData] = useState({});

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const { isDarkMode } = useTheme();
  const token = localStorage.getItem("token");
  const [pipelines, setPipelines] = useState([]);
  const [loadingPipelines, setLoadingPipelines] = useState(false);
  const [pipelineError, setPipelineError] = useState(null);

  const fetchPipelines = async () => {
    if (!formData.ghl_location_id) {
      setPipelineError("Location ID is required");
      return;
    }
    
    setLoadingPipelines(true);
    setPipelineError(null);
    try {
      const response = await apifetch('/s2/api/ghl/pipelines', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          api_token: formData.ghl_api_token === '••••••••' ? '' : formData.ghl_api_token,
          location_id: formData.ghl_location_id,
          api_version: formData.ghl_api_version,
          widget_key: widget?.widgetKey
        })
      });
      
      const data = await response.json();
      if (response.ok) {
        setPipelines(data.pipelines || []);
      } else {
        setPipelineError(data.error || 'Failed to fetch pipelines');
      }
    } catch (err) {
      setPipelineError('Failed to fetch pipelines');
      console.error(err);
    } finally {
      setLoadingPipelines(false);
    }
  };



  // Update form data when widget changes
  useEffect(() => {
    if (widget) {
      setFormData({
        title: widget.title || '',
        subtitle: widget.subtitle || '',
        buttonText: widget.buttonText || 'Start Security Scan',
        buttonColor: widget.buttonColor || '#8b5cf6',
        collectEmail: widget.collectEmail !== undefined ? widget.collectEmail : true,
        collectCompany: widget.collectCompany !== undefined ? widget.collectCompany : true,
        ghl_enabled: widget.ghl_enabled || false,
        layout: widget.layout || 'split',
        theme: widget.theme || 'light',
        displayMode: widget.displayMode || 'inline',
        features: widget.features || [],
        webhookUrl: widget.webhookUrl || '',
      });
    }
  }, [widget]);

  useEffect(() => {
    if (show && formData.ghl_location_id && (formData.ghl_has_token || formData.ghl_api_token)) {
      fetchPipelines();
    }
  }, [show, formData.ghl_location_id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const submitData = { ...formData };

      const response = await apifetch(`/s2/api/lead_magnets/${widget.widgetKey}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });

      if (!response.ok) {
        throw new Error('Failed to update widget');
      }

      const data = await response.json();
      onSave(data.widget);
      onHide();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!widget) return null;

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      contentClassName={isDarkMode ? "bg-dark text-light" : ""}
      className={isDarkMode ? 'dark-mode' : ''}
      data-bs-theme={isDarkMode ? 'dark' : 'light'}
      enforceFocus={false}
    >
      <Modal.Header closeButton className={isDarkMode ? 'bg-dark text-light border-secondary' : ''}>
        <Modal.Title>
          <MdEdit className="me-2" />
          Edit Widget - {widget.title}
        </Modal.Title>
      </Modal.Header>

      <form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" className="mb-3">
              <BiErrorCircle className="me-2" />
              {error}
            </Alert>
          )}

          <Row>
            <Col md={6}>
              <div className="mb-3">
                <label className={`form-label ${isDarkMode ? 'text-light' : ''}`}>Title *</label>
                <input
                  type="text"
                  className={`form-control ${isDarkMode ? 'bg-dark text-light border-secondary' : ''}`}
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                />
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <label className={`form-label ${isDarkMode ? 'text-light' : ''}`}>Subtitle</label>
                <input
                  type="text"
                  className={`form-control ${isDarkMode ? 'bg-dark text-light border-secondary' : ''}`}
                  name="subtitle"
                  value={formData.subtitle}
                  onChange={handleChange}
                />
              </div>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <div className="mb-3">
                <label className={`form-label ${isDarkMode ? 'text-light' : ''}`}>Button Text</label>
                <input
                  type="text"
                  className={`form-control ${isDarkMode ? 'bg-dark text-light border-secondary' : ''}`}
                  name="buttonText"
                  value={formData.buttonText}
                  onChange={handleChange}
                />
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <label className={`form-label ${isDarkMode ? 'text-light' : ''}`}>Button Color</label>
                <div className="d-flex gap-2">
                  <input
                    type="color"
                    className={`form-control form-control-color ${isDarkMode ? 'bg-dark border-secondary' : ''}`}
                    name="buttonColor"
                    value={formData.buttonColor}
                    onChange={handleChange}
                    style={{ width: '60px' }}
                  />
                  <input
                    type="text"
                    className={`form-control ${isDarkMode ? 'bg-dark text-light border-secondary' : ''}`}
                    name="buttonColor"
                    value={formData.buttonColor}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <div className="mb-3">
                <label className={`form-label ${isDarkMode ? 'text-light' : ''}`}>Layout</label>
                <select
                  className={`form-select ${isDarkMode ? 'bg-dark text-light border-secondary' : ''}`}
                  name="layout"
                  value={formData.layout}
                  onChange={handleChange}
                >
                  <option value="full">Full</option>
                  <option value="minimal">Bare Minimum</option>
                </select>
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <label className={`form-label ${isDarkMode ? 'text-light' : ''}`}>Theme</label>
                <select
                  className={`form-select ${isDarkMode ? 'bg-dark text-light border-secondary' : ''}`}
                  name="theme"
                  value={formData.theme}
                  onChange={handleChange}
                >
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                  <option value="cyber">Cyber</option>
                </select>
              </div>
            </Col>
          </Row>


          <Row>
            <Col md={12}>
              <div className="mb-3">
                <label className={`form-label ${isDarkMode ? 'text-light' : ''}`}>Legacy Marketing Webhook URL</label>
                <input
                  type="url"
                  className={`form-control ${isDarkMode ? 'bg-dark text-light border-secondary' : ''}`}
                  name="webhookUrl"
                  value={formData.webhookUrl}
                  onChange={handleChange}
                  placeholder="https://your-marketing-tool.com/webhook"
                />
                <div className={`form-text ${isDarkMode ? 'text-light-emphasis' : 'text-muted'}`}>
                  Data will be sent here when a report is unlocked
                </div>
              </div>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <div className="mb-3">
                <label className={`form-label ${isDarkMode ? 'text-light' : ''}`}>Display Mode</label>
                <select
                  className={`form-select ${isDarkMode ? 'bg-dark text-light border-secondary' : ''}`}
                  name="displayMode"
                  value={formData.displayMode}
                  onChange={handleChange}
                >
                  <option value="new-tab">New Tab</option>
                  <option value="inline">Inline Widget</option>
                </select>
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <div className="form-check mt-4">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    name="collectEmail"
                    id="collectEmail"
                    checked={formData.collectEmail}
                    onChange={handleChange}
                  />
                  <label className={`form-check-label ${isDarkMode ? 'text-light' : ''}`} htmlFor="collectEmail">
                    Collect Email Address
                  </label>
                </div>
              </div>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer className={isDarkMode ? 'bg-dark border-secondary' : ''}>
          <Button variant={isDarkMode ? "outline-light" : "secondary"} onClick={onHide}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="primary"
            disabled={saving}
          >
            {saving ? (
              <>
                <Spinner size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

function LeadMagnets() {
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedWidget, setSelectedWidget] = useState(null);
  const { isDarkMode } = useTheme();
  const token = localStorage.getItem("token");
  const mountedRef = useRef(true);

  // Fetch widgets
  const fetchWidgets = useCallback(async () => {
    if (!mountedRef.current) return;
    
    setLoading(true);
    setError(null);

    try {
      const response = await apifetch("/s2/api/lead_magnets", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error("Failed to fetch widgets");
      }

      const data = await response.json();
      if (mountedRef.current) {
        setWidgets(data.widgets || []);
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err.message);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [token]);

  // Set up mounted ref
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    fetchWidgets();
  }, [fetchWidgets]);

  // Delete widget
  const handleDelete = (widget) => {
    setSelectedWidget(widget);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedWidget) return;

    try {
      const response = await apifetch(`/s2/api/lead_magnets/${selectedWidget.widgetKey}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error("Failed to delete widget");
      }

      setWidgets(prev => prev.filter(w => w.widgetKey !== selectedWidget.widgetKey));
      setMessage("Lead magnet deleted successfully");
      setShowDeleteModal(false);
      setSelectedWidget(null);
    } catch (err) {
      setError(err.message);
    }
  };

  // Edit widget
  const handleEdit = (widget) => {
    setSelectedWidget(widget);
    setShowEditModal(true);
  };

  const handleSaveEdit = (updatedWidget) => {
    setWidgets(prev => prev.map(w => 
      w.widgetKey === updatedWidget.widgetKey ? updatedWidget : w
    ));
    setMessage("Widget updated successfully");
  };

  // Show info
  const handleShowInfo = (widget) => {
    setSelectedWidget(widget);
    setShowInfoModal(true);
  };

  // Auto-dismiss messages
  useEffect(() => {
    const timer = setTimeout(() => {
      setError(null);
      setMessage(null);
    }, 4000);
    return () => clearTimeout(timer);
  }, [error, message]);

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant={isDarkMode ? "light" : "primary"} />
        <p className={`mt-3 ${isDarkMode ? 'text-light-emphasis' : 'text-muted'}`}>
          Loading your lead magnets...
        </p>
      </div>
    );
  }

  return (
    <div className={`lead-magnets-container ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className={isDarkMode ? "text-light mb-0" : "mb-0"}>
          Lead Magnet Widgets
          {widgets.length > 0 && (
            <Badge bg="secondary" className="ms-2">
              {widgets.length}
            </Badge>
          )}
        </h5>

        <ButtonGroup size="sm">
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>Refresh widgets</Tooltip>}
          >
            <Button
              variant={isDarkMode ? "outline-light" : "outline-primary"}
              onClick={fetchWidgets}
              disabled={loading}
            >
              <MdRefresh className={loading ? "spin" : ""} />
            </Button>
          </OverlayTrigger>
        </ButtonGroup>
      </div>

      {error && (
        <Alert variant="danger" className="mb-3" dismissible onClose={() => setError(null)}>
          <BiErrorCircle className="me-2" />
          {error}
        </Alert>
      )}

      {message && (
        <Alert variant="success" className="mb-3" dismissible onClose={() => setMessage(null)}>
          <BiCheckCircle className="me-2" />
          {message}
        </Alert>
      )}

      {widgets.length === 0 ? (
        <div className="text-center py-5">
          <div className="mb-3">
            <MdInfoOutline size={48} className={isDarkMode ? 'text-light-emphasis' : 'text-muted'} />
          </div>
          <h5 className={isDarkMode ? 'text-light' : ''}>No Lead Magnets Found</h5>
          <p className={isDarkMode ? 'text-light-emphasis' : 'text-muted'}>
            You haven't created any lead magnet widgets yet.
          </p>
        </div>
      ) : (
        <div className="table-responsive">
          <Table
            striped
            bordered
            hover
            variant={isDarkMode ? "dark" : "light"}
            className="align-middle"
          >
            <thead>
              <tr>
                <th>#</th>
                <th>Key</th>
                <th>Button</th>
                <th>Display</th>
                <th>Theme</th>
                <th>Layout</th>
                <th>Created</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>

            <tbody>
              {widgets.map((widget, index) => (
                <tr key={widget.widgetKey}>
                  <td>{index + 1}</td>
                  <td>
                    <strong className={isDarkMode ? 'text-light' : ''}>{widget.widgetKey}</strong>
                  </td>
                  <td>
                    <Badge 
                      bg="primary"
                      style={{ backgroundColor: widget.buttonColor }}
                    >
                      {widget.buttonText || 'Scan Now'}
                    </Badge>
                  </td>
                  <td>
                    <Badge bg="info">
                      {widget.displayMode || 'inline'}
                    </Badge>
                  </td>
                  <td>
                    <Badge bg="secondary">
                      {widget.theme || 'light'}
                    </Badge>
                  </td>
                  <td>
                    <Badge bg="warning" text="dark">
                      {widget.layout || 'split'}
                    </Badge>
                  </td>
                  <td>
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>{new Date(widget.createdAt).toLocaleString()}</Tooltip>}
                    >
                      <span className={isDarkMode ? 'text-light' : ''}>
                        {new Date(widget.createdAt).toLocaleDateString()}
                      </span>
                    </OverlayTrigger>
                  </td>
                  <td className="text-center">
                    <ButtonGroup size="sm">
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>View integration guide</Tooltip>}
                      >
                        <Button
                          variant={isDarkMode ? "outline-light" : "outline-info"}
                          onClick={() => handleShowInfo(widget)}
                        >
                          <MdInfoOutline />
                        </Button>
                      </OverlayTrigger>

                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Edit widget</Tooltip>}
                      >
                        <Button
                          variant={isDarkMode ? "outline-light" : "outline-primary"}
                          onClick={() => handleEdit(widget)}
                        >
                          <MdEdit />
                        </Button>
                      </OverlayTrigger>

                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Delete widget</Tooltip>}
                      >
                        <Button
                          variant={isDarkMode ? "outline-light" : "outline-danger"}
                          onClick={() => handleDelete(widget)}
                        >
                          <MdDeleteSweep />
                        </Button>
                      </OverlayTrigger>
                    </ButtonGroup>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
        className={isDarkMode ? 'dark-mode' : ''}
        contentClassName={isDarkMode ? "bg-dark text-light" : ""}
        data-bs-theme={isDarkMode ? 'dark' : 'light'}
      >
        <Modal.Header closeButton className={isDarkMode ? 'bg-dark text-light border-secondary' : ''}>
          <Modal.Title className="text-danger">
            <MdDeleteSweep className="me-2" />
            Confirm Delete
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className={`text-center py-4 ${isDarkMode ? 'bg-dark text-light' : ''}`}>
          <MdDeleteSweep
            size={60}
            className="text-danger mb-3"
          />
          <h5>Delete "{selectedWidget?.title}"?</h5>
          <p className={isDarkMode ? 'text-light-emphasis mb-0' : 'text-muted mb-0'}>
            This action cannot be undone. This will permanently delete the<br />
            widget and all associated data.
          </p>
        </Modal.Body>

        <Modal.Footer className={isDarkMode ? 'bg-dark border-secondary' : ''}>
          <Button
            variant={isDarkMode ? "outline-light" : "outline-secondary"}
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={confirmDelete}
          >
            <MdDeleteSweep className="me-2" />
            Delete Permanently
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Widget Info Modal */}
      <WidgetInfoModal
        show={showInfoModal}
        onHide={() => {
          setShowInfoModal(false);
          setSelectedWidget(null);
        }}
        widget={selectedWidget}
      />

      {/* Edit Widget Modal */}
      <EditWidgetModal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setSelectedWidget(null);
        }}
        widget={selectedWidget}
        onSave={handleSaveEdit}
      />

      <style>
        {`
          .lead-magnets-container {
            padding: 1rem;
            transition: all 0.3s ease;
          }

          .lead-magnets-container.dark-mode {
            background-color: #1a1a1a;
            color: #ffffff;
          }

          .dark-mode .table {
            color: #ffffff;
            background-color: #2d2d2d;
          }

          .dark-mode .table thead th {
            background-color: #1a1a1a;
            color: #ffffff;
            border-color: #444;
          }

          .dark-mode .table td {
            border-color: #444;
            color: #ffffff;
          }

          .dark-mode .table-hover tbody tr:hover {
            background-color: #3d3d3d;
            color: #ffffff;
          }

          .dark-mode .table-striped tbody tr:nth-of-type(odd) {
            background-color: #2d2d2d;
          }

          .dark-mode .table-striped tbody tr:nth-of-type(even) {
            background-color: #333333;
          }

          .dark-mode .btn-outline-light {
            color: #ffffff;
            border-color: #ffffff;
          }

          .dark-mode .btn-outline-light:hover {
            background-color: #ffffff;
            color: #000000;
          }

          .dark-mode .form-control,
          .dark-mode .form-select {
            background-color: #333;
            border-color: #444;
            color: #fff;
          }

          .dark-mode .form-control:focus,
          .dark-mode .form-select:focus {
            background-color: #3d3d3d;
            border-color: #666;
            color: #fff;
          }

          .dark-mode .form-control::placeholder {
            color: #888;
          }

          .dark-mode .input-group-text {
            background-color: #333;
            border-color: #444;
            color: #fff;
          }

          .dark-mode .card {
            background-color: #2d2d2d;
            border-color: #444;
          }

          .dark-mode .card-body {
            color: #ffffff;
          }

          .dark-mode .nav-tabs .nav-link {
            color: #aaa;
          }

          .dark-mode .nav-tabs .nav-link.active {
            background-color: #2d2d2d;
            color: #fff;
            border-color: #444 #444 #2d2d2d;
          }

          .dark-mode .nav-pills .nav-link.active {
            background-color: #0d6efd;
          }

          .dark-mode .modal-header,
          .dark-mode .modal-footer {
            border-color: #444;
          }

          .dark-mode .modal-content {
            background-color: #2d2d2d;
          }

          .dark-mode .text-muted {
            color: #aaa !important;
          }

          .dark-mode .text-light-emphasis {
            color: #ddd !important;
          }

          .dark-mode code {
            background-color: #333;
            color: #ff6b6b;
          }

          .dark-mode .bg-light {
            background-color: #333 !important;
          }

          .dark-mode .bg-secondary {
            background-color: #444 !important;
          }

          .dark-mode .alert {
            background-color: #2d2d2d;
            color: #fff;
            border-color: #444;
          }

          .dark-mode .alert-success {
            background-color: #1e3a2e;
            color: #a3e9a3;
          }

          .dark-mode .alert-danger {
            background-color: #3a1e1e;
            color: #f8a5a5;
          }

          .spin {
            animation: spin 1s infinite linear;
          }

          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }

          .code-block {
            overflow-x: auto;
            white-space: pre-wrap;
            word-wrap: break-word;
            max-height: 300px;
          }

          .code-block code {
            color: #d3d2d2ff;
          }

          .widget-preview-container {
            overflow: hidden;
            border-radius: 8px;
          }

          .form-check-input:checked {
            background-color: #0d6efd;
            border-color: #0d6efd;
          }
        `}
      </style>
    </div>
  );
}

export default LeadMagnets;